<template>
  <v-card rounded="md" class="ma-4">
    <!-- Loading progress bar -->
    <v-progress-linear
      v-if="isLoadingReservations"
      indeterminate
      color="primary"
      height="4"
      class="position-absolute"
      style="top: 0; left: 0; right: 0; z-index: 10; border-radius: 4px 4px 0 0;"
    />

    <v-card-title class="d-flex align-center justify-space-between">
      <span>
        <v-icon class="mr-2">mdi-bookmark-check</v-icon>
        {{ tl('Incorporate Reservation E-Filing') }}
      </span>
      <div class="d-flex align-center gap-2">
        <EfilingWalkthroughButton />
        <!-- My Applications history button -->
        <v-btn
          v-if="myApplications.length > 0"
          variant="tonal"
          color="info"
          size="small"
          @click="showApplicationsDialog = true"
        >
          <v-icon start size="small">mdi-history</v-icon>
          {{ tl('My Applications') }} ({{ myApplications.length }})
        </v-btn>
        <v-chip v-if="readOnly" color="success" variant="flat" size="small">
          <v-icon start size="small">mdi-check-circle</v-icon>
          {{ tl('Completed') }}
        </v-chip>
        <!-- Test Mode toggle (non-production only) -->
        <v-chip
          v-if="!readOnly && eFiling.canUseTestMode"
          :color="eFiling.testMode.value ? 'warning' : 'grey-lighten-1'"
          :variant="eFiling.testMode.value ? 'flat' : 'outlined'"
          size="small"
          class="cursor-pointer"
          @click="eFiling.testMode.value = !eFiling.testMode.value"
        >
          <v-icon start size="small">mdi-test-tube</v-icon>
          {{ eFiling.testMode.value ? tl('Test Mode ON') : tl('Test Mode') }}
        </v-chip>
      </div>
    </v-card-title>
    <v-divider />

    <v-card-text>
      <!-- Loading Reservations indicator -->
      <v-alert
        v-if="isLoadingReservations"
        type="info"
        variant="tonal"
        class="mb-4"
        density="compact"
      >
        <div class="d-flex align-center gap-3">
          <v-progress-circular size="20" width="2" color="primary" indeterminate />
          <div>
            <div class="font-weight-medium">{{ tl('Loading Reservations') }}</div>
            <div class="text-caption text-grey-darken-1">{{ tl('Fetching approved reservations from SSM...') }}</div>
          </div>
        </div>
      </v-alert>

      <!-- Reservation Error -->
      <v-alert
        v-if="reservationError && reservationError === 'NO_RESERVATIONS'"
        type="warning"
        variant="tonal"
        class="mb-4"
        density="compact"
        closable
        @click:close="reservationError = null"
      >
        {{ tl('No Approved Reservations') }}
      </v-alert>

      <!-- SSM Login / Connection Error -->
      <v-card
        v-if="reservationError && reservationError !== 'NO_RESERVATIONS'"
        variant="outlined"
        color="error"
        class="mb-4"
      >
        <v-card-text class="pa-4">
          <div class="d-flex align-start gap-3">
            <v-icon color="error" size="36">mdi-server-network-off</v-icon>
            <div class="flex-grow-1">
              <div class="text-subtitle-1 font-weight-bold text-error mb-1">
                {{ tl('SSM System Connection Failed') }}
              </div>
              <div class="text-body-2 text-grey-darken-2 mb-1">
                {{ reservationError }}
              </div>
              <div class="text-caption text-grey">
                {{ tl('SSM MYCOID2016 system may be temporarily unavailable. Please try again later or contact support.') }}
              </div>
              <div class="d-flex gap-2 mt-3">
                <v-btn
                  color="primary"
                  variant="flat"
                  size="small"
                  :loading="isLoadingReservations"
                  @click="retryLoadReservations"
                >
                  <v-icon start size="16">mdi-refresh</v-icon>
                  {{ tl('Retry') }}
                </v-btn>
                <v-btn
                  variant="text"
                  size="small"
                  color="grey"
                  @click="reservationError = null"
                >
                  {{ tl('Dismiss') }}
                </v-btn>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Test Mode Warning -->
      <v-alert
        v-if="!readOnly && eFiling.testMode.value"
        type="warning"
        variant="tonal"
        class="mb-4"
        density="compact"
        border="start"
      >
        <div class="d-flex align-center gap-2">
          <v-icon size="18">mdi-test-tube</v-icon>
          <span class="font-weight-medium">{{ tl('Test Mode ON') }}</span>
          <span class="text-caption"> - {{ tl('All data will be entered into SSM but FormSave will NOT be executed.') }}</span>
        </div>
      </v-alert>

      <!-- Selected Reservation Info -->
      <v-alert
        v-if="selectedReservation"
        type="info"
        variant="tonal"
        class="mb-4"
        density="compact"
      >
        <div class="d-flex align-center gap-2">
          <v-icon size="18">mdi-bookmark-check</v-icon>
          <span class="font-weight-bold">{{ selectedReservation.entity_name }}</span>
          <v-chip v-if="selectedReservation.reservation_number" size="x-small" variant="outlined">{{ selectedReservation.reservation_number }}</v-chip>
        </div>
        <!-- Pre-registered Directors -->
        <div v-if="selectedReservation.directors?.length" class="mt-2">
          <div class="d-flex align-center gap-1 text-body-2 text-grey-darken-2 mb-1">
            <v-icon size="14">mdi-account-tie</v-icon>
            <span>{{ tl('Registered Directors') }} ({{ selectedReservation.directors.length }})</span>
          </div>
          <div v-for="(dir, idx) in selectedReservation.directors" :key="idx" class="mb-1">
            <v-chip size="small" variant="outlined" color="primary" class="mr-1">
              {{ dir.person_name }}
              <span v-if="dir.identification_number" class="ml-1 text-grey">({{ dir.identification_type || 'ID' }}: {{ dir.identification_number }})</span>
            </v-chip>
            <span v-if="dir.date_of_birth || dir.email" class="text-caption text-grey-darken-1">
              <span v-if="dir.date_of_birth">{{ dir.date_of_birth }}</span>
              <span v-if="dir.date_of_birth && dir.email"> | </span>
              <span v-if="dir.email">{{ dir.email }}</span>
            </span>
          </div>
        </div>
      </v-alert>

      <!-- Incorporate Reservation Form -->
      <IncorporateReservationForm
        ref="irFormRef"
        :task-config="taskConfig"
        :task-id="taskId"
        :loading="eFiling.isSubmitting.value"
        :read-only="readOnly"
        :reservation-list="reservationList"
        :loading-reservations="isLoadingReservations"
        @update:formData="handleFormDataUpdate"
        @reservation-select="handleReservationSelect"
      />

      <!-- Validation Errors -->
      <v-alert
        v-if="!readOnly && eFiling.validationErrors.value.length > 0"
        type="error"
        variant="tonal"
        class="mt-4"
        closable
        @click:close="eFiling.validationErrors.value = []"
      >
        <div class="font-weight-bold mb-2">{{ tl('Validation Errors') }}:</div>
        <div v-for="(error, index) in eFiling.validationErrors.value" :key="index"
          :class="error.startsWith('  -') ? 'text-body-2 pl-4 text-error' : 'font-weight-medium mt-1'"
          :style="error.startsWith('  -') ? 'opacity: 0.85' : ''"
        >{{ error }}</div>
      </v-alert>

      <!-- Completed task: PDF only -->
      <v-card
        v-if="readOnly && eFiling.pdfDataBase64.value"
        variant="tonal"
        color="success"
        class="mt-4 pa-4"
      >
        <div class="d-flex align-center mb-3">
          <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
          <span class="text-h6 font-weight-bold text-success">
            {{ tl('E-Filing Completed') }}
          </span>
        </div>

        <v-alert
          v-if="eFiling.warningMessage.value"
          type="warning"
          variant="tonal"
          class="mb-3"
          density="compact"
          border="start"
        >
          <div class="d-flex align-start gap-2">
            <v-icon size="16" color="warning">mdi-alert</v-icon>
            <div class="text-body-2">{{ eFiling.warningMessage.value }}</div>
          </div>
        </v-alert>

        <v-btn
          color="primary"
          variant="flat"
          @click="handleDownloadPdf"
        >
          <v-icon start>mdi-file-pdf-box</v-icon>
          {{ tl('Download Certificate') }}
        </v-btn>
      </v-card>
    </v-card-text>

    <v-divider />

    <v-card-actions class="justify-end pa-4">
      <v-btn
        v-if="showBackButton"
        variant="outlined"
        color="secondary"
        :disabled="eFiling.isSubmitting.value || eFiling.isSaving.value"
        @click="emit('back')"
      >
        <v-icon start>mdi-arrow-left</v-icon>
        {{ tl('Back') }}
      </v-btn>
      <v-spacer />
      <v-btn
        v-if="!readOnly"
        variant="outlined"
        color="primary"
        :loading="eFiling.isSaving.value"
        :disabled="eFiling.isSubmitting.value || eFiling.isSaving.value"
        class="mr-2"
        @click="eFiling.handleTempSave(taskId, irFormRef, readOnly)"
      >
        <v-icon start>mdi-content-save-outline</v-icon>
        {{ tl('Temp Save') }}
      </v-btn>
      <v-btn
        v-if="!readOnly"
        color="primary"
        variant="flat"
        :loading="eFiling.isSubmitting.value"
        :disabled="eFiling.isSubmitting.value || eFiling.isSaving.value"
        @click="eFiling.handleSubmit(irFormRef, readOnly, (msg) => emit('error', msg))"
      >
        <v-icon start>mdi-send</v-icon>
        {{ tl('Submit E-Filing') }}
      </v-btn>
    </v-card-actions>

    <!-- Credential Select Dialog -->
    <EFilingCredentialDialog
      v-model="eFiling.showCredentialDialog.value"
      :mycoid-credentials="eFiling.mycoidCredentials.value"
      :selected-id="eFiling.selectedCredentialId.value"
      :is-loading="eFiling.isLoadingCredentials.value"
      :guide-message="reservationLoadMode
        ? tl('IR Credential For Loading')
        : tl('IR Credential Required')"
      @select="eFiling.selectCredential"
      @confirm="handleCredentialDialogConfirm"
      @manage="eFiling.openManageCredential"
    />

    <!-- Credential Manage Modal -->
    <CredentialManageModal
      v-model="eFiling.showManageCredentialDialog.value"
      @credential-added="eFiling.handleCredentialAdded"
    />

    <!-- Confirm Dialog -->
    <EFilingConfirmDialog
      v-model="eFiling.showConfirmDialog.value"
      :credential="eFiling.selectedCredential.value"
      :message="tl('IR Confirm Message')"
      :show-test-mode="eFiling.canUseTestMode"
      :test-mode="eFiling.testMode.value"
      @update:test-mode="eFiling.testMode.value = $event"
      @confirm="handleConfirmSubmit"
    />

    <!-- Process Dialog -->
    <EFilingProcessDialog
      v-model="eFiling.showProcessDialog.value"
      :step="eFiling.submitStep.value"
      :success="eFiling.resultSuccess.value"
      :message="eFiling.resultMessage.value"
      :pdf-data-base64="eFiling.pdfDataBase64.value"
      :warning-message="eFiling.warningMessage.value"
      :success-title="eFiling.testMode.value ? tl('Test Mode Completed') : tl('Incorporation Submitted Successfully')"
      :fail-title="tl('Incorporation Failed')"
      @close="eFiling.handleProcessDialogClose(router)"
      @download-pdf="handleDownloadPdf"
    >
      <template #result>
        <div v-if="eFiling.testMode.value" class="text-body-1 text-grey-darken-1 mt-2">
          {{ tl('Test mode - All form data was entered into SSM but FormSave was NOT executed. No charges applied.') }}
        </div>
        <div v-else class="text-body-1 text-grey-darken-1 mt-2">
          {{ tl('Incorporation Success Message') }}
        </div>

        <!-- Dry Run Verification -->
        <div v-if="dryRunVerification" class="text-left mt-4">
          <v-divider class="mb-3" />
          <div class="text-subtitle-2 font-weight-bold mb-2">
            <v-icon size="18" class="mr-1">mdi-clipboard-check-outline</v-icon>
            {{ tl('Verification Result') }}
          </div>

          <!-- Application Details -->
          <div v-if="dryRunVerification.tabs?.application_details" class="mb-3">
            <div class="text-caption font-weight-bold text-primary mb-1">{{ tl('Application Details') }}</div>
            <div v-if="dryRunVerification.tabs.application_details.business_description" class="text-body-2">
              <span class="text-grey-darken-1">{{ tl('Business') }}: </span>{{ dryRunVerification.tabs.application_details.business_description }}
            </div>
            <div v-if="dryRunVerification.tabs.application_details.email" class="text-body-2">
              <span class="text-grey-darken-1">{{ tl('Email') }}: </span>{{ dryRunVerification.tabs.application_details.email }}
            </div>
          </div>

          <!-- Directors -->
          <div v-if="dryRunVerification.tabs?.directors" class="mb-3">
            <div class="text-caption font-weight-bold text-primary mb-1">
              {{ tl('Directors') }} ({{ dryRunVerification.tabs.directors.count || 0 }})
            </div>
            <v-table v-if="dryRunVerification.tabs.directors.items?.length" density="compact" class="text-body-2">
              <thead><tr>
                <th>{{ tl('Name') }}</th><th>{{ tl('ID Type') }}</th><th>{{ tl('ID No') }}</th><th>{{ tl('Nationality') }}</th>
              </tr></thead>
              <tbody>
                <tr v-for="(d, i) in dryRunVerification.tabs.directors.items" :key="i">
                  <td>{{ d.name }}</td><td>{{ d.id_type }}</td><td>{{ d.id_number }}</td><td>{{ d.nationality }}</td>
                </tr>
              </tbody>
            </v-table>
          </div>

          <!-- Shareholders -->
          <div v-if="dryRunVerification.tabs?.shareholders" class="mb-3">
            <div class="text-caption font-weight-bold text-primary mb-1">
              {{ tl('Shareholders') }} ({{ dryRunVerification.tabs.shareholders.count || 0 }})
            </div>
            <v-alert v-if="dryRunVerification.tabs.shareholders.empty" type="warning" variant="tonal" density="compact" class="text-body-2">
              {{ tl('No shareholders registered') }}
            </v-alert>
            <v-table v-else-if="dryRunVerification.tabs.shareholders.items?.length" density="compact" class="text-body-2">
              <thead><tr>
                <th>{{ tl('Name') }}</th><th>{{ tl('ID No') }}</th><th>{{ tl('Price/Share') }}</th><th>{{ tl('Shares') }}</th><th>{{ tl('Class') }}</th>
              </tr></thead>
              <tbody>
                <tr v-for="(s, i) in dryRunVerification.tabs.shareholders.items" :key="i">
                  <td>{{ s.name }}</td><td>{{ s.id_number }}</td>
                  <td>{{ s.price_per_share }}</td><td>{{ s.number_of_shares }}</td><td>{{ s.class_of_share }}</td>
                </tr>
              </tbody>
            </v-table>
          </div>

          <!-- Info To Agency -->
          <div v-if="dryRunVerification.tabs?.information_to_agency?.lodger_name">
            <div class="text-caption font-weight-bold text-primary mb-1">{{ tl('Lodger') }}</div>
            <div class="text-body-2">
              {{ dryRunVerification.tabs.information_to_agency.lodger_name }}
              <span v-if="dryRunVerification.tabs.information_to_agency.lodger_id" class="text-grey">
                ({{ dryRunVerification.tabs.information_to_agency.lodger_id }})
              </span>
            </div>
          </div>
        </div>
      </template>
    </EFilingProcessDialog>

    <!-- Temp Save Dialog -->
    <EFilingTempSaveDialog
      v-model="eFiling.showTempSaveDialog.value"
      :success="eFiling.tempSaveSuccess.value"
      :message="eFiling.tempSaveMessage.value"
    />

    <!-- My Applications Dialog -->
    <v-dialog v-model="showApplicationsDialog" max-width="850" scrollable>
      <v-card>
        <v-card-title class="bg-primary text-white d-flex align-center py-3">
          <v-icon class="mr-2" size="22">mdi-clipboard-list</v-icon>
          <span class="text-subtitle-1 font-weight-bold">{{ tl('My Application - Name Reservation') }}</span>
          <v-spacer />
          <v-chip variant="elevated" color="white" size="small" class="text-primary font-weight-bold">
            {{ myApplications.length }}
          </v-chip>
        </v-card-title>
        <div class="d-flex align-center gap-3 px-4 py-3">
          <v-text-field
            v-model="appSearchQuery"
            prepend-inner-icon="mdi-magnify"
            :placeholder="tl('Search company name or reservation no.')"
            density="compact"
            variant="outlined"
            hide-details
            clearable
            style="max-width: 360px;"
            @click:clear="appSearchQuery = ''"
          />
          <v-select
            v-model="appStatusFilter"
            :items="appStatusOptions"
            density="compact"
            variant="outlined"
            hide-details
            style="max-width: 160px;"
          >
            <template #selection="{ item }">
              <v-chip v-if="item.value !== 'ALL'" size="x-small" variant="tonal" :color="getStatusColor(item.value)" label class="mr-1">{{ item.value }}</v-chip>
              <span v-else class="text-body-2">{{ tl('All Status') }}</span>
            </template>
          </v-select>
          <v-spacer />
          <span class="text-body-2 text-grey">{{ filteredApplications.length }} / {{ myApplications.length }}</span>
        </div>
        <v-divider />
        <v-card-text class="pa-0" style="max-height: 55vh; overflow-y: auto;">
          <v-table density="comfortable" hover class="app-history-table">
            <thead>
              <tr class="bg-grey-lighten-4">
                <th class="text-body-2 font-weight-bold text-grey-darken-2" style="width: 36px;">#</th>
                <th class="text-body-2 font-weight-bold text-grey-darken-2">{{ tl('Company Name') }}</th>
                <th class="text-body-2 font-weight-bold text-grey-darken-2" style="width: 150px;">{{ tl('Reservation No.') }}</th>
                <th class="text-body-2 font-weight-bold text-grey-darken-2" style="width: 140px;">{{ tl('Purpose') }}</th>
                <th class="text-body-2 font-weight-bold text-grey-darken-2" style="width: 140px;">{{ tl('Company Type') }}</th>
                <th class="text-body-2 font-weight-bold text-grey-darken-2 text-center" style="width: 110px;">{{ tl('Status') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(app, idx) in filteredApplications" :key="idx">
                <td class="text-body-2 text-grey">{{ idx + 1 }}</td>
                <td class="text-body-2 font-weight-medium">{{ app.entity_name }}</td>
                <td class="text-body-2 text-grey-darken-1">{{ app.reservation_number || '-' }}</td>
                <td class="text-body-2 text-grey-darken-1">{{ app.purpose || '-' }}</td>
                <td class="text-body-2 text-grey-darken-1">{{ app.company_type || '-' }}</td>
                <td class="text-center">
                  <v-chip
                    v-if="app.status && app.status.trim()"
                    size="small"
                    variant="tonal"
                    :color="getStatusColor(app.status)"
                    label
                    class="font-weight-medium"
                    style="min-width: 80px;"
                  >
                    {{ app.status }}
                  </v-chip>
                </td>
              </tr>
              <tr v-if="filteredApplications.length === 0">
                <td colspan="6" class="text-center text-body-2 text-grey pa-6">
                  {{ appSearchQuery || appStatusFilter !== 'ALL' ? tl('No matching applications.') : tl('No applications found.') }}
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn variant="tonal" color="primary" @click="showApplicationsDialog = false; appSearchQuery = ''; appStatusFilter = 'ALL'">
            <v-icon start size="18">mdi-close</v-icon>
            {{ tl('Close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import IncorporateReservationForm from './IncorporateReservationForm.vue';
import { incorporateReservationApi, type ReservationItem, type MyApplicationItem, type DryRunVerification } from '../../api/incorporateReservationApi';
import { buildPayload } from './composables/useFormPayload';
import { updateEFilingComplete, saveEFilingData } from '@/features/startTask/api/taskApi';
import CredentialManageModal from '@/features/Entity_my/components/credentials/CredentialManageModal.vue';
import { useEFilingSubmit } from '../../composables/useEFilingSubmit';
import { downloadPdf } from '../../composables/usePdfDownload';
import EFilingCredentialDialog from '../shared/EFilingCredentialDialog.vue';
import EfilingWalkthroughButton from '../shared/EfilingWalkthroughButton.vue';
import EFilingConfirmDialog from '../shared/EFilingConfirmDialog.vue';
import EFilingProcessDialog from '../shared/EFilingProcessDialog.vue';
import EFilingTempSaveDialog from '../shared/EFilingTempSaveDialog.vue';

const router = useRouter();
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);
const tl = (text: string) => {
  if (!text) return '';
  const translated = t(`startTask.${text}`);
  return (translated && translated !== `startTask.${text}`) ? translated : text;
};

// Props
interface Props {
  taskData?: any;
  taskId?: string;
  showBackButton?: boolean;
  readOnly?: boolean;
  taskConfig?: any;
}

const props = withDefaults(defineProps<Props>(), {
  taskData: null,
  taskId: undefined,
  showBackButton: true,
  readOnly: false,
  taskConfig: null,
});

const emit = defineEmits<{
  back: [];
  submit: [data: any];
  error: [message: string];
}>();

// Composable
const eFiling = useEFilingSubmit({ formType: 'M32004' });

// Form ref
const irFormRef = ref<InstanceType<typeof IncorporateReservationForm> | null>(null);
const formDataRef = ref<any>(null);
const dryRunVerification = ref<DryRunVerification | null>(null);

// Reservation list state
const reservationLoadMode = ref(false);
const reservationList = ref<ReservationItem[]>([]);
const isLoadingReservations = ref(false);
const selectedReservation = ref<ReservationItem | null>(null);
const reservationError = ref<string | null>(null);

// My Applications (전체 신청 이력)
const myApplications = ref<MyApplicationItem[]>([]);
const showApplicationsDialog = ref(false);
const appSearchQuery = ref('');
const appStatusFilter = ref('ALL');
const appStatusOptions = computed(() => {
  const statuses = new Set(myApplications.value.map(a => a.status).filter(s => s && s.trim()));
  return ['ALL', ...Array.from(statuses).sort()];
});
const filteredApplications = computed(() => {
  let list = myApplications.value;
  if (appStatusFilter.value !== 'ALL') {
    list = list.filter(a => a.status === appStatusFilter.value);
  }
  const q = (appSearchQuery.value || '').trim().toLowerCase();
  if (q) {
    list = list.filter(a =>
      a.entity_name.toLowerCase().includes(q) ||
      a.reservation_number.toLowerCase().includes(q)
    );
  }
  return list;
});
const getStatusColor = (status: string) => {
  switch (status) {
    case 'APPROVED': return 'success';
    case 'PENDING': return 'warning';
    case 'REJECTED': return 'error';
    case 'EXPIRED': return 'grey';
    default: return 'info';
  }
};

// Load saved data on mount
onMounted(async () => {
  if (props.taskData?.formData) {
    setTimeout(() => {
      if (irFormRef.value?.loadSavedData) {
        irFormRef.value.loadSavedData(props.taskData.formData);
      }
    }, 100);
  }

  if (props.readOnly && props.taskData?.eFilingResult) {
    const result = props.taskData.eFilingResult;
    eFiling.pdfDataBase64.value = result.pdfDataBase64 || null;
    eFiling.warningMessage.value = result.warningMessage || null;
  }

  // readOnly가 아닌 경우: 크리덴셜 다이얼로그 표시하여 예약 목록 조회
  if (!props.readOnly) {
    reservationLoadMode.value = true;
    await eFiling.loadCredentials();
    eFiling.showCredentialDialog.value = true;
  }
});

watch(() => props.taskData, (newData) => {
  if (newData?.formData && irFormRef.value?.loadSavedData) {
    irFormRef.value.loadSavedData(newData.formData);
  }
}, { deep: true });

// SSM 로그인 → 예약 목록 조회 (공통 로직, 타임아웃 포함)
const SSM_LOGIN_TIMEOUT = 30000; // 30초
const withTimeout = <T>(promise: Promise<T>, ms: number, message: string): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error(message)), ms)),
  ]);
};

const loadReservations = async () => {
  isLoadingReservations.value = true;
  reservationError.value = null;
  try {
    const loginResponse: any = await withTimeout(
      incorporateReservationApi.ssmLogin(eFiling.selectedCredentialId.value || ''),
      SSM_LOGIN_TIMEOUT,
      tl('SSM login timed out')
    );
    if (!loginResponse.success || !loginResponse.session_id) {
      throw new Error(loginResponse.error || tl('SSM Login failed.'));
    }
    const result = await withTimeout(
      incorporateReservationApi.getReservationList(loginResponse.session_id),
      SSM_LOGIN_TIMEOUT,
      tl('Reservation list request timed out.')
    );
    if (result.success) {
      reservationList.value = result.reservations || [];
      myApplications.value = result.all_applications || [];
      if (reservationList.value.length === 0) {
        reservationError.value = 'NO_RESERVATIONS';
      }
    } else {
      reservationError.value = result.error || tl('Failed to load reservation list.');
    }
  } catch (error: any) {
    reservationError.value = error?.message || tl('Failed to load reservation list.');
  } finally {
    isLoadingReservations.value = false;
    reservationLoadMode.value = false;
  }
};

const retryLoadReservations = () => {
  loadReservations();
};

const handleCredentialDialogConfirm = async () => {
  if (reservationLoadMode.value) {
    eFiling.showCredentialDialog.value = false;
    await loadReservations();
  } else {
    // 제출 모드 (기존 로직)
    eFiling.handleCredentialConfirm(props.readOnly);
  }
};

const handleReservationSelect = (item: ReservationItem) => {
  selectedReservation.value = item;
};

const handleFormDataUpdate = (data: any) => {
  formDataRef.value = data;
};

const handleDownloadPdf = () => {
  if (eFiling.pdfDataBase64.value) {
    downloadPdf(
      eFiling.pdfDataBase64.value,
      `IncorporateReservation_certificate`,
    );
  }
};

// Incorporate Reservation specific submit logic
const handleConfirmSubmit = async () => {
  if (props.readOnly) {
    eFiling.showConfirmDialog.value = false;
    return;
  }

  const formData = irFormRef.value?.getFormData();
  if (!formData) {
    eFiling.validationErrors.value = [tl('Failed to get form data')];
    emit('error', tl('Failed to get form data'));
    return;
  }

  eFiling.startProcessing();

  try {
    // Step 1: SSM Login
    const loginResponse: any = await incorporateReservationApi.ssmLogin(eFiling.selectedCredentialId.value || '');

    if (!loginResponse.success || !loginResponse.session_id) {
      throw new Error(loginResponse.error || tl('SSM Login failed. Please check your credential.'));
    }

    const sessionId = loginResponse.session_id;

    // Step 2: Build payload and call API
    const entityName = selectedReservation.value?.entity_name || formData?.reservedName?.reservedEntityName || '';
    const entityNumber = selectedReservation.value?.reservation_number || formData?.reservedName?.reservedEntityNumber || '';

    // Debug: Log raw director data before payload build
    console.log('[IR-EFiling] Raw formData.directors:', JSON.stringify(formData.directors?.map((d: any) => ({
      name: d.name, gender: d.gender, race: d.race, nationality: d.nationality, dob: d.dob, email: d.email, idType: d.idType
    })), null, 2));

    const payload = await buildPayload(
      formData,
      sessionId,
      entityName,
      entityNumber,
      eFiling.selectedCredentialId.value || undefined
    );

    // Test mode: dry_run
    if (eFiling.testMode.value) {
      payload.dry_run = true;
    }

    // Debug: Log payload directors after build
    console.log('[IR-EFiling] Payload directors:', JSON.stringify(payload.directors?.map(d => ({
      person_name: d.person_name, gender: d.gender, race: d.race, nationality: d.nationality, date_of_birth: d.date_of_birth, email: d.email
    })), null, 2));
    if (payload.dry_run) {
      console.log('[IR-EFiling] DRY RUN mode - FormSave will NOT be executed');
    }

    const response = await incorporateReservationApi.incorporateReservation(payload);

    const apiData = (response as any)?.data || response;

    if (apiData?.success) {
      // dry_run 검증 데이터 저장
      dryRunVerification.value = apiData.dry_run_verification || null;

      if (eFiling.testMode.value) {
        // Test mode: show success but don't update task or navigate away
        eFiling.completeSuccess(tl('Test Mode Result Message'));
        eFiling.isSubmitSuccess.value = false; // Prevent navigation to TaskList
      } else {
        // Real submission
        eFiling.pdfDataBase64.value = apiData.pdf_data_base64 || apiData.pdfDataBase64 || null;
        eFiling.warningMessage.value = apiData.warning || null;

        eFiling.completeSuccess(tl('Company has been incorporated successfully!'));

        if (props.taskId) {
          try {
            await updateEFilingComplete(props.taskId);
          } catch (updateError) {
            console.error('[IncorporateReservationEFiling] Task status update failed:', updateError);
          }
        }
        try {
          await saveEFilingData(props.taskId ?? null, {
            taskTypeCd: 'M32004',
            entityCd: null,
            countryCd: 'MY',
            formData: formData as Record<string, unknown>,
            responseData: apiData as Record<string, unknown>,
            warningMessage: eFiling.warningMessage.value || null,
            receiptPdfBase64: eFiling.pdfDataBase64.value || null,
            receiptPdfFilename: 'IncorporateReservation_certificate.pdf',
            status: 'B36003',
            isSuccess: true,
            completedAt: new Date().toISOString(),
          });
        } catch (saveError) {
          console.error('[IncorporateReservationEFiling] L_TASK_EFILING_DATA save failed:', saveError);
        }

        emit('submit', apiData);
      }
    } else {
      eFiling.completeFromApiResult(apiData, '', tl('Incorporate reservation failed'));
    }
  } catch (error: any) {
    const errorMessage = eFiling.completeError(error, tl('Failed to incorporate'));
    emit('error', errorMessage);
  }
};
</script>
